import React, { useState } from "react";
import {
  AiFillFacebook,
  AiFillInstagram,
  AiFillYoutube,
  AiOutlineTwitter,
  AiOutlineMail,
  AiOutlineCheck,
} from "react-icons/ai";
import {
  HiOutlineLocationMarker,
  HiOutlinePhone,
  HiOutlineShieldCheck,
  HiOutlineTruck,
  HiOutlineStar,
  HiOutlineHeart,
} from "react-icons/hi";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../../server";
import { footerProductLinks, footercompanyLinks, footerSupportLinks } from "../../static/data";
import { useSiteSettings } from "../../hooks/useSiteSettings";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { settings: siteSettings } = useSiteSettings();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${server}/newsletter/subscribe`, { email });
      if (response.data.success) {
        setIsSubscribed(true);
        setEmail("");
        toast.success(response.data.message || "Successfully subscribed!");
        setTimeout(() => setIsSubscribed(false), 3000);
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative">
      {/* Trust Badges Bar */}
      <div className="bg-white border-t border-secondary-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <HiOutlineTruck className="w-6 h-6 text-accent-500" />
              <span className="text-sm font-medium text-gray-700">Free Shipping</span>
              <span className="text-xs text-gray-500">Orders above Rs.499</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <HiOutlineShieldCheck className="w-6 h-6 text-accent-500" />
              <span className="text-sm font-medium text-gray-700">100% Natural</span>
              <span className="text-xs text-gray-500">Lab tested ingredients</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <HiOutlineHeart className="w-6 h-6 text-accent-500" />
              <span className="text-sm font-medium text-gray-700">Cruelty Free</span>
              <span className="text-xs text-gray-500">No animal testing</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <HiOutlineStar className="w-6 h-6 text-accent-500" />
              <span className="text-sm font-medium text-gray-700">Premium Quality</span>
              <span className="text-xs text-gray-500">Handcrafted with care</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="bg-primary-800 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand Info */}
            <div className="space-y-5">
              <Link to="/" className="inline-block">
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-secondary-300">Positive</span>
                  <span className="text-2xl font-bold text-accent-400">Moments</span>
                </div>
              </Link>
              <p className="text-white/60 text-sm leading-relaxed">
                {siteSettings?.companyInfo?.description ||
                  "Premium herbal and handcrafted products sourced from the heart of India. Bringing authentic Ayurvedic wellness to your doorstep."}
              </p>
              <div className="space-y-2.5 text-sm">
                <div className="flex items-start gap-2.5 text-white/50 hover:text-white/80 transition-colors">
                  <HiOutlineLocationMarker className="w-4 h-4 mt-0.5 text-secondary-300 flex-shrink-0" />
                  <span>
                    {siteSettings?.footerAddress?.streetAddress || "India"}
                  </span>
                </div>
                <div className="flex items-center gap-2.5 text-white/50 hover:text-white/80 transition-colors">
                  <HiOutlinePhone className="w-4 h-4 text-secondary-300 flex-shrink-0" />
                  <span>{siteSettings?.footerAddress?.phone || "+91 00000 00000"}</span>
                </div>
                <div className="flex items-center gap-2.5 text-white/50 hover:text-white/80 transition-colors">
                  <AiOutlineMail className="w-4 h-4 text-secondary-300 flex-shrink-0" />
                  <span>{siteSettings?.footerAddress?.email || "hello@positivemoments.in"}</span>
                </div>
              </div>
            </div>

            {/* Shop Links */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-5 relative">
                Shop
                <div className="absolute -bottom-1.5 left-0 w-8 h-0.5 bg-accent-500"></div>
              </h3>
              <ul className="space-y-2.5">
                {footerProductLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.link}
                      onClick={scrollToTop}
                      className="text-white/50 hover:text-secondary-300 transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-5 relative">
                Company
                <div className="absolute -bottom-1.5 left-0 w-8 h-0.5 bg-accent-500"></div>
              </h3>
              <ul className="space-y-2.5">
                {footercompanyLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.link}
                      onClick={scrollToTop}
                      className="text-white/50 hover:text-secondary-300 transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support & Social */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-5 relative">
                Support
                <div className="absolute -bottom-1.5 left-0 w-8 h-0.5 bg-accent-500"></div>
              </h3>
              <ul className="space-y-2.5 mb-6">
                {footerSupportLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.link || "#"}
                      onClick={scrollToTop}
                      className="text-white/50 hover:text-secondary-300 transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Social Media */}
              <h4 className="text-sm font-semibold text-white mb-3">Follow Us</h4>
              <div className="flex gap-2.5">
                {[
                  { icon: AiFillFacebook, link: siteSettings?.socialMedia?.facebook || "#", bg: "hover:bg-blue-600" },
                  { icon: AiFillInstagram, link: siteSettings?.socialMedia?.instagram || "#", bg: "hover:bg-pink-600" },
                  { icon: AiOutlineTwitter, link: siteSettings?.socialMedia?.twitter || "#", bg: "hover:bg-sky-500" },
                  { icon: AiFillYoutube, link: siteSettings?.socialMedia?.youtube || "#", bg: "hover:bg-red-600" },
                ].map((social, index) => (
                  <a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-9 h-9 bg-white/10 ${social.bg} rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110`}
                  >
                    <social.icon className="w-4 h-4 text-white" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-primary-900 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-white/40 text-sm">
              &copy; {new Date().getFullYear()}{" "}
              <span className="text-white/70 font-medium">
                {siteSettings?.companyInfo?.name || "Positive Moments"}
              </span>
              . All rights reserved.
            </p>
            <div className="flex flex-wrap gap-5 text-sm">
              <Link to="/terms" onClick={scrollToTop} className="text-white/40 hover:text-secondary-300 transition-colors">
                Terms
              </Link>
              <Link to="/privacy" onClick={scrollToTop} className="text-white/40 hover:text-secondary-300 transition-colors">
                Privacy
              </Link>
              <Link to="/refund" onClick={scrollToTop} className="text-white/40 hover:text-secondary-300 transition-colors">
                Refund Policy
              </Link>
              <Link to="/shipping" onClick={scrollToTop} className="text-white/40 hover:text-secondary-300 transition-colors">
                Shipping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
